import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Mui5 from "components/mui5";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import TemplateBuilder from "./components/TemplateBuilder";
import FileUploadArea from "./components/FileUploadArea";
import ValidationAlerts from "./components/ValidationAlerts";
import ConfirmationScreen from "./components/ConfirmationScreen";
import useOneRepMaxCsvTemplateGenerator from "./hooks/useOneRepMaxCsvTemplateGenerator";
import useOneRepMaxCsvParser from "./hooks/useOneRepMaxCsvParser";
import useOneRepMaxValidator from "./hooks/useOneRepMaxValidator";
import useBulkOneRepMaxUpload from "./hooks/useBulkOneRepMaxUpload";
import useAthleteStore from "providers/AthletesProvider/useAthleteStore";
import useExercises from "providers/ExercisesProvider/useExercises";
import usePreferences from "hooks/usePreferences";
import Div from "components/primitives/Div";

interface BulkOneRepMaxUploadDialogProps {
  open: boolean;
  onClose: () => void;
  selectedAthleteIds: string[];
}

type UploadStep = "template" | "upload" | "validation" | "confirmation";

function BulkOneRepMaxUploadDialog(props: BulkOneRepMaxUploadDialogProps) {
  const { open, onClose, selectedAthleteIds } = props;
  const { t } = useTranslation();

  const [currentStep, setCurrentStep] = useState<UploadStep>("template");
  const [selectedExerciseIds, setSelectedExerciseIds] = useState<string[]>([]);
  const [parsedData, setParsedData] = useState<any>(null);

  const allAthletes = useAthleteStore();
  const exercises = useExercises();
  const [{ imperialSystemEnabled }, { toggleImperialSystem }] = usePreferences();

  const athletes = useMemo(
    () => allAthletes.filter((athlete) => selectedAthleteIds.includes(athlete.id)),
    [allAthletes, selectedAthleteIds]
  );

  const { generateTemplate } = useOneRepMaxCsvTemplateGenerator();
  const { parseFile } = useOneRepMaxCsvParser();
  const { validate: validateData } = useOneRepMaxValidator();
  const { uploadOneRepMaxes: uploadData } = useBulkOneRepMaxUpload();

  const steps = useMemo(
    () => [
      { key: "template", label: t("athlete.bulk_1rm_step_template") },
      { key: "upload", label: t("athlete.bulk_1rm_step_upload") },
      { key: "validation", label: t("athlete.bulk_1rm_step_validation") },
      { key: "confirmation", label: t("athlete.bulk_1rm_step_confirmation") },
    ],
    [t]
  );

  const currentStepIndex = useMemo(() => steps.findIndex((step) => step.key === currentStep), [currentStep, steps]);

  const selectedExercises = useMemo(
    () => exercises.filter((exercise) => selectedExerciseIds.includes(exercise.id)),
    [exercises, selectedExerciseIds]
  );

  const validationResult = useMemo(() => {
    if (!parsedData) {
      return {
        missingAthletes: [],
        missingExercises: [],
        invalidValues: [],
        canProceed: false,
        validAthleteCount: 0,
        exerciseCount: 0,
        validData: [],
      };
    }
    return validateData(parsedData, allAthletes, exercises);
  }, [parsedData, allAthletes, exercises, validateData]);

  const handleExercisesChange = useCallback((exerciseIds: string[]) => {
    setSelectedExerciseIds(exerciseIds);
  }, []);

  const handleDownloadTemplate = useCallback(() => {
    const csvContent = generateTemplate({ athletes, exercises: selectedExercises, imperialSystemEnabled });

    // Create a blob and download it
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "1rm-upload-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [athletes, selectedExercises, imperialSystemEnabled, generateTemplate]);

  const handleContinueToUpload = useCallback(() => {
    setCurrentStep("upload");
  }, []);

  const handleFileSelect = useCallback(
    async (file: File) => {
      try {
        const data = await parseFile(file);
        setParsedData(data);
        setCurrentStep("validation");
      } catch (error) {
        console.error("Failed to parse CSV:", error);
      }
    },
    [parseFile]
  );

  const handleContinueToConfirmation = useCallback(() => {
    setCurrentStep("confirmation");
  }, []);

  const handleBack = useCallback(() => {
    const stepKeys: UploadStep[] = ["template", "upload", "validation", "confirmation"];
    const currentIndex = stepKeys.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepKeys[currentIndex - 1]);
    }
  }, [currentStep]);

  const handleConfirm = useCallback(async () => {
    if (!validationResult) return;

    try {
      await uploadData(validationResult);
      onClose();
      // Reset state
      setCurrentStep("template");
      setSelectedExerciseIds([]);
      setParsedData(null);
    } catch (error) {
      console.error("Failed to upload 1RM data:", error);
    }
  }, [validationResult, uploadData, onClose]);

  const handleCloseDialog = useCallback(() => {
    // Reset state on close
    setCurrentStep("template");
    setSelectedExerciseIds([]);
    setParsedData(null);
    onClose();
  }, [onClose]);

  const isTemplateDownloadDisabled = useMemo(
    () => selectedExerciseIds.length === 0 || athletes.length === 0,
    [selectedExerciseIds.length, athletes.length]
  );

  const canProceedFromValidation = useMemo(
    () => validationResult.missingExercises.length === 0 && validationResult.invalidValues.length === 0,
    [validationResult.missingExercises.length, validationResult.invalidValues.length]
  );

  const totalUpdates = useMemo(() => {
    if (!validationResult || !validationResult.validData) return 0;
    return validationResult.validData.reduce((total, athleteData) => total + athleteData.oneRepMaxes.length, 0);
  }, [validationResult]);

  const athleteCount = useMemo(() => validationResult?.validAthleteCount || 0, [validationResult]);

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
      <DialogTitle>
        <Div sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {t("athlete.bulk_1rm_upload_dialog_title")}
          <IconButton onClick={handleCloseDialog} aria-label={t("global.close_label")}>
            <CloseIcon />
          </IconButton>
        </Div>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={currentStepIndex} sx={{ mb: 4 }}>
          {steps.map((step) => (
            <Step key={step.key}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {currentStep === "template" && (
          <TemplateBuilder
            selectedExerciseIds={selectedExerciseIds}
            onExercisesChange={handleExercisesChange}
            onDownloadTemplate={handleDownloadTemplate}
            isDownloadDisabled={isTemplateDownloadDisabled}
            imperialSystemEnabled={imperialSystemEnabled}
          />
        )}

        {currentStep === "upload" && (
          <FileUploadArea
            onFileSelect={handleFileSelect}
            imperialSystemEnabled={imperialSystemEnabled}
            onToggleMeasurementSystem={toggleImperialSystem}
          />
        )}

        {currentStep === "validation" && (
          <Div sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <ValidationAlerts
              missingAthletes={validationResult.missingAthletes}
              missingExercises={validationResult.missingExercises}
              invalidValues={validationResult.invalidValues}
            />
          </Div>
        )}

        {currentStep === "confirmation" && (
          <ConfirmationScreen
            athleteCount={athleteCount}
            exerciseCount={validationResult.exerciseCount}
            totalUpdates={totalUpdates}
            onConfirm={handleConfirm}
            onCancel={handleBack}
            isSubmitting={false}
          />
        )}
      </DialogContent>

      {currentStep !== "confirmation" && (
        <DialogActions>
          {currentStep !== "template" && (
            <Mui5.Button variant="outlined" onClick={handleBack}>
              {t("global.go_back_button_text")}
            </Mui5.Button>
          )}
          <Mui5.Button variant="outlined" onClick={handleCloseDialog}>
            {t("global.cancel_button_text")}
          </Mui5.Button>
          {currentStep === "template" && (
            <Mui5.Button variant="contained" onClick={handleContinueToUpload}>
              {t("athlete.bulk_1rm_continue_to_upload")}
            </Mui5.Button>
          )}
          {currentStep === "validation" && (
            <Mui5.Button
              variant="contained"
              onClick={handleContinueToConfirmation}
              disabled={!canProceedFromValidation}
            >
              {t("global.continue")}
            </Mui5.Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}

export default BulkOneRepMaxUploadDialog;
